import { FORM_PHASE, VoucherAmountType } from "../../../../config/constant";

/**
 * Get amount from inventoryDetails by selected typology and amount key
 */
export const getAmountFromInventory = (inventoryDetails, selectedTypology, amountKey) => {
  const inventory = inventoryDetails || [];
  const inventoryItem = inventory.find((inv) => inv?.type === selectedTypology);
  return inventoryItem?.[amountKey] ?? null;
};

/**
 * Get voucher amount - BHK Wise from inventory or Fixed from top-level
 */
export const getVoucherAmount = (eoiCampaignDetails, selectedTypology) => {
  const isBhkWise = eoiCampaignDetails?.voucherAmountType === VoucherAmountType.BHK_WISE;
  if (isBhkWise) {
    return getAmountFromInventory(eoiCampaignDetails?.inventoryDetails, selectedTypology, "voucherAmt");
  }
  return eoiCampaignDetails?.voucherAmount ?? null;
};

/**
 * Get standard EOI amount - BHK Wise from inventory or Fixed from top-level
 */
export const getStdEoiAmount = (eoiCampaignDetails, selectedTypology) => {
  const isBhkWise = eoiCampaignDetails?.stdEoiAmountType === VoucherAmountType.BHK_WISE;
  if (isBhkWise) {
    return getAmountFromInventory(eoiCampaignDetails?.inventoryDetails, selectedTypology, "standardEOIAmt");
  }
  return eoiCampaignDetails?.stdEoiAmount ?? null;
};

/**
 * Get preferential EOI amount - BHK Wise from inventory or Fixed from top-level
 */
export const getPreEoiAmount = (eoiCampaignDetails, selectedTypology) => {
  const isBhkWise = eoiCampaignDetails?.preEoiAmountType === VoucherAmountType.BHK_WISE;
  if (isBhkWise) {
    return getAmountFromInventory(eoiCampaignDetails?.inventoryDetails, selectedTypology, "preferentialEOIAmt");
  }
  return eoiCampaignDetails?.preEoiAmount ?? null;
};

/**
 * Check if voucher amount exists (for validation/visibility)
 */
export const hasVoucherAmount = (eoiCampaignDetails) => {
  const isBhkWise = eoiCampaignDetails?.voucherAmountType === VoucherAmountType.BHK_WISE;
  if (isBhkWise) {
    const inventory = eoiCampaignDetails?.inventoryDetails || [];
    return inventory.some((inv) => inv?.voucherAmt != null);
  }
  return Boolean(eoiCampaignDetails?.voucherAmount);
};

/**
 * Check if standard EOI amount exists
 */
export const hasStdEoiAmount = (eoiCampaignDetails) => {
  const isBhkWise = eoiCampaignDetails?.stdEoiAmountType === VoucherAmountType.BHK_WISE;
  if (isBhkWise) {
    const inventory = eoiCampaignDetails?.inventoryDetails || [];
    return inventory.some((inv) => inv?.standardEOIAmt != null);
  }
  return Boolean(eoiCampaignDetails?.stdEoiAmount);
};

/**
 * Check if preferential EOI amount exists
 */
export const hasPreEoiAmount = (eoiCampaignDetails) => {
  const isBhkWise = eoiCampaignDetails?.preEoiAmountType === VoucherAmountType.BHK_WISE;
  if (isBhkWise) {
    const inventory = eoiCampaignDetails?.inventoryDetails || [];
    return inventory.some((inv) => inv?.preferentialEOIAmt != null);
  }
  return Boolean(eoiCampaignDetails?.preEoiAmount);
};

/**
 * Get payable amount for payment step - returns string for consistency with existing usage
 */
export const getPayableAmount = (applicantData, eoiCampaignDetails, isUpgradePreferentialStep = false) => {
  if (!applicantData || !eoiCampaignDetails) return "0";

  const { formPhase, eoiDetails } = applicantData;
  const eoiType = eoiDetails?.eoiType;
  const selectedTypology = eoiDetails?.typology;

  if (formPhase === FORM_PHASE.VOUCHER) {
    if (!isUpgradePreferentialStep) {
      const amt = getVoucherAmount(eoiCampaignDetails, selectedTypology);
      return amt == null ? "0" : String(amt);
    }
  }
  if (formPhase === FORM_PHASE.EOI) {
    if (eoiType === "Preferential") {
      const amt = getPreEoiAmount(eoiCampaignDetails, selectedTypology);
      return amt == null ? "0" : String(amt);
    }
    if (eoiType === "Standard") {
      const amt = getStdEoiAmount(eoiCampaignDetails, selectedTypology);
      return amt == null ? "0" : String(amt);
    }
  }

  return (
    eoiCampaignDetails?.preEoiAmount ||
    eoiCampaignDetails?.stdEoiAmount ||
    eoiCampaignDetails?.voucherAmount ||
    "0"
  );
};
